/**
 * Doserne Profile — Overview + Menu customization + Kurs (placeholder) + Devices.
 */
import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, User as UserIcon, Menu as MenuIcon, GraduationCap, Smartphone,
  Eye, EyeOff, ArrowUp, ArrowDown, CheckCircle2, XCircle, Shield, Phone, FileText, Lock, KeyRound,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import DoserneShell from "./DoserneShell";
import ContractTab from "./ContractTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU_LABELS = {
  home:     "Hjem",
  plan:     "Daglig plan",
  projects: "Prosjekter",
  progress: "Daglig progresjon",
  underlag: "Underlag",
  kontroll: "Kontroll",
  sja:      "SJA",
  lager:    "Lager (snart)",
  kurs:     "Kurs (snart)",
  profile:  "Min profil",
};

function TabBtn({ active, onClick, Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold border-b-2 transition ${active ? "border-cyan-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function OverviewTab({ user, authHeader, onRefresh }) {
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [phone, setPhone]       = useState(user?.phone || "");
  const [saving, setSaving]     = useState(false);

  // Keep local state in sync when the parent user object refreshes.
  useEffect(() => {
    setFullName(user?.full_name || "");
    setPhone(user?.phone || "");
  }, [user?.full_name, user?.phone]);

  const trimmedName  = fullName.trim();
  const trimmedPhone = phone.trim();
  const dirty  = trimmedName !== (user?.full_name || "") || trimmedPhone !== (user?.phone || "");
  const canSave = dirty && trimmedName.length >= 2 && trimmedPhone.length >= 3;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await axios.put(`${API}/doserne/prefs`, {
        full_name: trimmedName,
        phone: trimmedPhone,
      }, authHeader());
      toast.success("Profil oppdatert");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    }
    setSaving(false);
  };

  const initial = (fullName || user?.full_name || user?.email || "?").trim()[0]?.toUpperCase();

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-slate-900 flex items-center justify-center text-lg font-black" style={{ color: "#fff" }}>
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-lg font-black text-slate-900 truncate">{fullName || user.email}</div>
          <div className="text-xs text-slate-500">{user.is_admin ? "Administrator" : "Doserne Academy medlem"}</div>
        </div>
      </div>

      {/* Read-only email */}
      <div className="border-t border-slate-100 pt-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">E-post</div>
        <div className="text-sm text-slate-900 font-mono">{user.email}</div>
      </div>

      {/* Editable fields */}
      <div className="space-y-3">
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1 inline-flex items-center gap-1">
            <UserIcon className="w-3 h-3" /> Fullt navn
          </div>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Elvis Popescu"
            className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm text-slate-900 bg-white focus:outline-none focus:border-cyan-500"
            data-testid="profile-name-input"
          />
        </label>

        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1 inline-flex items-center gap-1">
            <Phone className="w-3 h-3" /> Telefon
          </div>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+47 900 00 000"
            className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm text-slate-900 bg-white focus:outline-none focus:border-cyan-500"
            data-testid="profile-phone-input"
          />
        </label>
      </div>

      {/* Save button — only visible when there are unsaved changes */}
      {dirty && (
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={() => { setFullName(user?.full_name || ""); setPhone(user?.phone || ""); }}
            className="px-3 py-2 text-xs font-semibold text-slate-500 hover:text-slate-900"
            data-testid="profile-reset"
            type="button"
          >
            Avbryt
          </button>
          <button
            onClick={save}
            disabled={saving || !canSave}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-not-allowed"
            style={{ color: "#fff" }}
            data-testid="profile-save"
            type="button"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Lagre endringer
          </button>
        </div>
      )}

      <PasswordSection authHeader={authHeader} hasPassword={user?.has_password} onRefresh={onRefresh} />
    </div>
  );
}

/**
 * PasswordSection — lets a Doserne member set/change their password.
 * Password login is preferred over OTP for workers using in-app browsers
 * (e.g. Messenger) because OTP codes can be sent to the wrong browser session.
 */
function PasswordSection({ authHeader, hasPassword, onRefresh }) {
  const [expanded, setExpanded] = React.useState(false);
  const [pwd, setPwd] = React.useState("");
  const [pwd2, setPwd2] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const submit = async () => {
    if (pwd.length < 6) { toast.error("Passordet må være minst 6 tegn"); return; }
    if (pwd !== pwd2) { toast.error("Passordene stemmer ikke"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/doserne/me/set-password`, { password: pwd }, authHeader());
      toast.success(hasPassword ? "Passord oppdatert" : "Passord satt — du kan nå logge inn med det");
      setPwd(""); setPwd2(""); setExpanded(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre passord");
    }
    setSaving(false);
  };

  return (
    <div className="border-t border-slate-100 pt-4">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 text-left group"
        data-testid="pwd-section-toggle"
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${hasPassword ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          <Lock className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-black text-slate-900">
            {hasPassword ? "Endre passord" : "Sett passord for rask innlogging"}
          </div>
          <div className="text-[11px] text-slate-500">
            {hasPassword
              ? "Du kan logge inn med e-post + passord (raskere enn kode)"
              : "Anbefalt om du åpner appen fra Messenger eller andre in-app nettlesere"}
          </div>
        </div>
        <ArrowDown className={`w-4 h-4 text-slate-400 transition ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="mt-3 space-y-2 pl-10">
          <input
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="Nytt passord (min 6 tegn)"
            autoComplete="new-password"
            className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm text-slate-900 bg-white focus:outline-none focus:border-cyan-500"
            data-testid="pwd-input-1"
          />
          <input
            type="password"
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            placeholder="Bekreft passord"
            autoComplete="new-password"
            className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm text-slate-900 bg-white focus:outline-none focus:border-cyan-500"
            data-testid="pwd-input-2"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setPwd(""); setPwd2(""); setExpanded(false); }}
              className="px-3 py-2 text-xs font-semibold text-slate-500 hover:text-slate-900"
              data-testid="pwd-cancel"
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving || pwd.length < 6 || pwd !== pwd2}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400"
              style={{ color: "#fff" }}
              data-testid="pwd-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Lagre passord
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuTab({ authHeader, isAdmin }) {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/menu-prefs`, authHeader());
        setItems(data.items || []);
      } catch { setItems([]); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const move = (i, dir) => {
    const target = i + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[i], next[target]] = [next[target], next[i]];
    setItems(next);
  };
  const toggle = (i) => {
    const next = [...items];
    next[i] = { ...next[i], visible: next[i].visible === false ? true : false };
    setItems(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/doserne/menu-prefs`, { items }, authHeader());
      toast.success("Meny lagret");
    } catch { toast.error("Kunne ikke lagre"); }
    setSaving(false);
  };
  const saveAsGlobal = async () => {
    if (!window.confirm("Setează această ordine ca implicită pentru toți colegii care nu au personalizat meniul lor?")) return;
    setSavingGlobal(true);
    try {
      // Save personal first so admin's view stays consistent
      await axios.put(`${API}/doserne/menu-prefs`, { items }, authHeader());
      // Then push as global default
      await axios.put(`${API}/doserne/menu-prefs/global-default`, { items }, authHeader());
      toast.success("Salvat ca meniu implicit pentru toți colegii ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre som global");
    }
    setSavingGlobal(false);
  };
  const resetToDefault = async () => {
    if (!window.confirm("Șterge personalizarea ta și folosește ordinea implicită setată de administrator?")) return;
    setResetting(true);
    try {
      const { data } = await axios.post(`${API}/doserne/menu-prefs/reset`, {}, authHeader());
      setItems(data.items || []);
      toast.success("Menyen er tilbakestilt");
    } catch { toast.error("Kunne ikke tilbakestille"); }
    setResetting(false);
  };

  if (!items) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div>
      <div className="text-xs text-slate-500 mb-3">
        Reorganiser eller skjul menyoppføringer.
        {isAdmin
          ? " Som administrator kan du og lagre den som standard for hele teamet."
          : " Endringene lagres for din bruker."}
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 mb-3">
        {items.map((it, i) => {
          const visible = it.visible !== false;
          return (
            <div key={it.id} className="flex items-center gap-2 px-4 py-3" data-testid={`menu-pref-${it.id}`}>
              <span className={`w-2 h-2 rounded-full ${visible ? "bg-emerald-500" : "bg-slate-300"}`} />
              <span className={`flex-1 text-sm font-semibold ${visible ? "text-slate-900" : "text-slate-400"}`}>{MENU_LABELS[it.id] || it.id}</span>
              <button onClick={() => toggle(i)} className="p-2 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100" data-testid={`toggle-${it.id}`} title={visible ? "Skjul" : "Vis"}>
                {visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`up-${it.id}`}>
                <ArrowUp className="w-4 h-4" />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`down-${it.id}`}>
                <ArrowDown className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
      <div className="space-y-2">
        <button
          onClick={save}
          disabled={saving}
          className="w-full py-2.5 bg-slate-900 disabled:bg-slate-400 rounded-lg text-sm font-semibold shadow-sm inline-flex items-center justify-center gap-2"
          style={{ color: "#fff" }}
          data-testid="save-menu"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Lagre for meg"}
        </button>

        {isAdmin && (
          <button
            onClick={saveAsGlobal}
            disabled={savingGlobal}
            className="w-full py-2.5 border-2 border-amber-500 text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 rounded-lg text-sm font-black uppercase tracking-widest shadow-sm inline-flex items-center justify-center gap-2"
            data-testid="save-menu-global"
            title="Aplică ordinea aceasta pentru toți colegii care nu au personalizat meniul"
          >
            {savingGlobal ? <Loader2 className="w-4 h-4 animate-spin" /> : "👑 Setează pentru toată echipa"}
          </button>
        )}

        <button
          onClick={resetToDefault}
          disabled={resetting}
          className="w-full py-2 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center justify-center gap-1"
          data-testid="reset-menu"
        >
          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "↺ Tilbakestill til standardmenyen"}
        </button>
      </div>
    </div>
  );
}

function KursTab() {
  const { authHeader } = useDoserneAuth();
  const [certs, setCerts] = useState(null);
  useEffect(() => {
    axios.get(`${API}/doserne/certificates/mine`, authHeader())
      .then(({ data }) => setCerts(data.certificates || []))
      .catch(() => setCerts([]));
  }, [authHeader]);

  return (
    <div className="space-y-4" data-testid="profile-kurs-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-black text-slate-900 leading-tight">Mine kursbevis</h3>
          <p className="text-sm text-slate-500">Diplomele mele · My certificates</p>
        </div>
        <Link to="/doserne/kursbevis" className="text-sm font-bold text-orange-600 hover:text-orange-700" data-testid="profile-view-all-certs">Åpne alle →</Link>
      </div>

      {certs === null && (
        <div className="p-6 rounded-2xl border border-slate-200 bg-white text-slate-500 text-sm">…</div>
      )}
      {certs && certs.length === 0 && (
        <div className="p-6 rounded-2xl border-2 border-dashed border-slate-200 bg-white text-center text-slate-500 text-sm" data-testid="profile-kurs-empty">
          Ingen kursbevis ennå. Fullfør et kurs og signer bekreftelsen for å motta det første.
        </div>
      )}
      {certs && certs.length > 0 && (
        <div className="space-y-3">
          {certs.slice(0, 5).map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-slate-200" data-testid="profile-cert-row">
              <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center flex-none">
                <GraduationCap className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-slate-900 truncate">
                  {typeof c.course_title === "object"
                    ? (c.course_title.no || c.course_title.en || Object.values(c.course_title)[0] || "—")
                    : c.course_title || "—"}
                </div>
                <div className="text-[11px] font-mono text-slate-500">{c.cert_number} · {c.issued_at ? new Date(c.issued_at).toLocaleDateString("no-NO") : ""}</div>
              </div>
              <a href={`${API}/doserne/certificates/${c.id}/pdf`} target="_blank" rel="noreferrer" className="text-xs font-bold text-orange-600 hover:text-orange-700">PDF</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DevicesTab({ authHeader }) {
  const [devices, setDevices] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/devices/mine`, authHeader());
        setDevices(data.devices || []);
      } catch { setDevices([]); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (devices === null) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (devices.length === 0) return <div className="text-sm text-slate-500 text-center py-6">Ingen enheter registrert enda.</div>;

  return (
    <div className="space-y-2">
      {devices.map((d) => (
        <div key={d.device_id} className="bg-white border border-slate-200 rounded-xl p-3 text-sm" data-testid={`device-${d.device_id}`}>
          <div className="flex items-start gap-3">
            <Smartphone className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-slate-900 font-semibold text-xs break-all">{d.user_agent}</div>
              <div className="text-xs text-slate-500 mt-1">
                Sist aktiv: {new Date(d.last_seen).toLocaleString("no-NO")}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DoserneProfile() {
  const { user, loading, authHeader, refresh } = useDoserneAuth();
  const [tab, setTab] = useState("overview");

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  return (
    <DoserneShell title="Min profil" subtitle="Innstillinger, meny og kurs">
      <div className="border-b border-slate-200 mb-4 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} Icon={UserIcon}       label="Oversikt" testid="tab-overview" />
          <TabBtn active={tab === "contract"} onClick={() => setTab("contract")} Icon={FileText}      label="Kontrakt" testid="tab-contract" />
          <TabBtn active={tab === "menu"}     onClick={() => setTab("menu")}     Icon={MenuIcon}       label="Meny"     testid="tab-menu" />
          <TabBtn active={tab === "kurs"}     onClick={() => setTab("kurs")}     Icon={GraduationCap}  label="Kurs"     testid="tab-kurs" />
          <TabBtn active={tab === "devices"}  onClick={() => setTab("devices")}  Icon={Smartphone}     label="Enheter"  testid="tab-devices" />
        </div>
      </div>

      {tab === "overview" && <OverviewTab user={user} authHeader={authHeader} onRefresh={refresh} />}
      {tab === "contract" && <ContractTab authHeader={authHeader} />}
      {tab === "menu"     && <MenuTab authHeader={authHeader} isAdmin={user.is_admin} />}
      {tab === "kurs"     && <KursTab />}
      {tab === "devices"  && <DevicesTab authHeader={authHeader} />}

      {/* Hidden admin link — only rendered for admins, tucked at the
          bottom below the tab content so it stays out of the way but
          reachable when needed. This is the entry point to the
          "Important" break-glass panel for anonymous reports. */}
      {user.is_admin && (
        <div className="mt-6 pt-4 border-t border-dashed border-slate-200 flex flex-col gap-2">
          <a
            href="/doserne/admin/kontrakter"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-black text-sky-700 hover:text-sky-900"
            data-testid="profile-contracts-link"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-sky-500" /> Kontrakter — tildel &amp; varsle
          </a>
          <a
            href="/doserne/admin/important"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-black text-rose-600 hover:text-rose-800"
            data-testid="profile-important-link"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> Important — anonyme rapporter
          </a>
        </div>
      )}
    </DoserneShell>
  );
}
